import React from 'react';
import moment from 'moment';
import { DateFormats } from 'constants/common';
import { MUITable, MUIBaseEntity, MUITableColumn, MUITableSorting } from 'components/shared/MUITable';
import { ActionsCell } from './cells/ActionsCell';

export interface MenuTemplateTableEntity extends MUIBaseEntity {
  id: string;
  name: string;
  dealers: string;
  canDelete: number;
  sharedFrom: string;
  description: string;
  createdDateTime: string;
  modifiedDateTime: string;
}

interface MenuTemplatesTableProps {
  isLoading: boolean;
  sorting: MUITableSorting;
  hasWriteAccess: boolean;
  hasLinkedFromMenus: boolean;
  onEdit: (id: string) => void;
  isAftermarketPartner: boolean;
  onDelete: (id: string) => void;
  tableData: MenuTemplateTableEntity[];
}

interface CreateColumnsParams {
  hasWriteAccess: boolean;
  hasLinkedFromMenus: boolean;
  onEdit: (id: string) => void;
  isAftermarketPartner: boolean;
  onDelete: (id: string) => void;
}

const createColumns = ({
  onEdit,
  onDelete,
  hasWriteAccess,
  hasLinkedFromMenus,
  isAftermarketPartner,
}: CreateColumnsParams): MUITableColumn<MenuTemplateTableEntity>[] => {
  const columns: MUITableColumn<MenuTemplateTableEntity>[] = [
    {
      header: 'Name',
      renderKey: 'name',
      sortKey: 'name',
    },
    {
      header: 'Description',
      renderKey: 'description',
    },
  ];

  if (isAftermarketPartner) {
    columns.push({
      header: 'Dealers',
      renderKey: 'dealers',
    });
  }

  if (hasLinkedFromMenus) {
    columns.push({
      header: 'Shared From',
      renderKey: 'sharedFrom',
      sortKey: 'sharedFrom',
    });
  }

  columns.push(
    {
      header: 'Created',
      renderKey: 'createdDateTime',
      sortKey: 'createdDateTime',
      renderCell: ({ createdDateTime }) =>
        createdDateTime ? moment(createdDateTime).format(DateFormats.DateTimeShort12Hour) : '-',
    },
    {
      header: 'Modified',
      renderKey: 'modifiedDateTime',
      sortKey: 'modifiedDateTime',
      renderCell: ({ modifiedDateTime }) =>
        modifiedDateTime ? moment(modifiedDateTime).format(DateFormats.DateTimeShort12Hour) : '-',
    },
    {
      header: 'Action',
      renderKey: 'id',
      renderCell: entity => (
        <ActionsCell
          onEdit={() => onEdit(entity.id as string)}
          onDelete={hasWriteAccess && entity.canDelete ? () => onDelete(entity.id as string) : undefined}
        />
      ),
    }
  );

  return columns;
};

export const MenuTemplatesTable = ({
  onEdit,
  sorting,
  onDelete,
  tableData,
  isLoading,
  hasWriteAccess,
  hasLinkedFromMenus,
  isAftermarketPartner,
}: MenuTemplatesTableProps) => {
  const columns = createColumns({
    onEdit,
    onDelete,
    hasWriteAccess,
    hasLinkedFromMenus,
    isAftermarketPartner,
  });

  return (
    <MUITable<MenuTemplateTableEntity>
      hover
      stickyHeader
      columns={columns}
      sorting={sorting}
      tableData={tableData}
      isLoading={isLoading}
    />
  );
};
